import type { ImageAIProvider, ProviderInfo, PollResult } from '../../types/providers';
import type { OperationType } from '../../types/operations';

const HF_MODELS: Partial<Record<OperationType, string>> = {
  upscale: 'caidas/swin2SR-classical-sr-x2-64',
  remove_bg: 'briaai/RMBG-1.4',
};

export class HuggingFaceProvider implements ImageAIProvider {
  readonly info: ProviderInfo = {
    id: 'huggingface',
    name: 'HuggingFace',
    tier: 'free',
    supportedOps: ['upscale', 'remove_bg'],
    requiresApiKey: true,
    apiKeyUrl: 'https://huggingface.co/settings/tokens',
  };

  private apiToken: string;
  private baseUrl = 'https://api-inference.huggingface.co/models';
  // Store results keyed by a fake prediction ID
  private results = new Map<string, { blob: Blob; status: 'succeeded' | 'failed'; error?: string }>();

  constructor(apiToken: string) {
    this.apiToken = apiToken;
  }

  async healthCheck(): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/caidas/swin2SR-classical-sr-x2-64`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${this.apiToken}` },
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  async submit(
    operation: OperationType,
    inputImage: Blob,
    options?: { scale?: number; [key: string]: unknown }
  ): Promise<string> {
    const modelId = HF_MODELS[operation];
    if (!modelId) throw new Error(`HuggingFace does not support operation: ${operation}`);

    const predictionId = `hf_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

    // HF Inference API is synchronous — fire the request and store the result
    this.runInference(modelId, inputImage, predictionId, operation, options).catch(() => {});

    return predictionId;
  }

  private async runInference(
    modelId: string,
    inputImage: Blob,
    predictionId: string,
    operation: OperationType,
    options?: { scale?: number; [key: string]: unknown }
  ): Promise<void> {
    try {
      // For upscale with 4x, chain two 2x passes
      const scale = options?.scale ?? 2;
      let currentBlob = inputImage;
      const passes = operation === 'upscale' && scale >= 4 ? 2 : 1;

      for (let i = 0; i < passes; i++) {
        const res = await fetch(`${this.baseUrl}/${modelId}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.apiToken}`,
            'Content-Type': inputImage.type || 'image/jpeg',
          },
          body: currentBlob,
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: res.statusText }));
          // Model loading — HF returns 503 while loading
          if (res.status === 503) {
            const estimated = (err as { estimated_time?: number }).estimated_time || 30;
            throw new Error(`Model is loading, estimated ${Math.ceil(estimated)}s. Try again shortly.`);
          }
          throw new Error((err as { error?: string }).error || `HuggingFace API error: ${res.status}`);
        }

        currentBlob = await res.blob();
      }

      this.results.set(predictionId, { blob: currentBlob, status: 'succeeded' });
    } catch (err) {
      this.results.set(predictionId, {
        blob: new Blob(),
        status: 'failed',
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  async poll(predictionId: string): Promise<PollResult> {
    const result = this.results.get(predictionId);
    if (!result) {
      return { status: 'processing' };
    }

    if (result.status === 'failed') {
      return { status: 'failed', error: result.error };
    }

    // Convert blob to object URL for output
    const url = URL.createObjectURL(result.blob);
    return { status: 'succeeded', output: [url] };
  }

  async cancel(_predictionId: string): Promise<void> {
    // HF inference is synchronous — can't cancel, just discard result
  }

  /** Direct access to the result blob (avoids double-URL conversion) */
  getResultBlob(predictionId: string): Blob | undefined {
    return this.results.get(predictionId)?.blob;
  }

  clearResult(predictionId: string): void {
    this.results.delete(predictionId);
  }
}
